import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import { useMarketIndicators } from "../../context/MarketIndicatorContext";
import { useExport } from "../../hooks/useExport";
import { InfoTooltip } from "../Helper/InfoToolTip";
import { TOOLTIPS } from "../../helper/tooltilData";
import {
  useGlobalLiveFeed,
  liveCrossoverStatus,
} from "../../hooks/useGlobalLiveFeed";
import "./SmartMoneyScreener.css";

const TIMEFRAMES = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

// const UNIVERSE_META = {
//   NASDAQ100: {
//     title: "NASDAQ 100 Crossover",
//     subtitle:
//       "MACD crossovers with SL, target and status across NASDAQ 100 constituents",
//   },
//   SP500: {
//     title: "S&P 500 Crossover",
//     subtitle:
//       "MACD crossovers with SL, target and status across S&P 500 constituents",
//   },
// };

// ── helpers (identical to NSE version) ──
const UNIVERSE_META = {
  NASDAQ100: {
    label: "NASDAQ 100",
    title: "Global Crossover",
    subtitle:
      "MACD crossovers with SL, target and status across NASDAQ 100 constituents",
  },
  SP500: {
    label: "S&P 500",
    title: "Global Crossover",
    subtitle:
      "MACD crossovers with SL, target and status across S&P 500 constituents",
  },
  DOWJONES: {
    label: "Dow Jones",
    title: "Global Crossover",
    subtitle:
      "MACD crossovers with SL, target and status across Dow Jones constituents",
  },
};

const getBadgeClass = (signal) => {
  const s = signal?.toUpperCase?.();
  if (s === "BUY" || s === "BULLISH") return "sm__signal--strong";
  if (s === "SELL" || s === "BEARISH") return "sm__signal--weak";
  return "sm__signal--moderate";
};

const getCrossoverClass = (crossover) => {
  if (crossover === "bullish") return "sm__clr-bull";
  if (crossover === "bearish") return "sm__clr-bear";
  return "sm__clr-neutral";
};

const getMetrics = (stock, timeframe) => {
  const p = stock?.predictions?.[timeframe];
  if (!p) return null;
  return {
    rsi: p.rsi != null ? Number(Number(p.rsi).toFixed(2)) : null,
    macd: p.macd != null ? Number(Number(p.macd).toFixed(2)) : null,
    macd_signal:
      p.macd_signal != null ? Number(Number(p.macd_signal).toFixed(2)) : null,
    histogram:
      p.histogram != null ? Number(Number(p.histogram).toFixed(2)) : null,
    prediction: p.prediction || "Neutral",
    prediction_result: p.prediction_result || "—",
    macd_crossover: p.macd_crossover || "none",
    crossover_high: p.crossover_high ?? null,
    crossover_low: p.crossover_low ?? null,
    crossover_close: p.crossover_close ?? null,
    crossover_date: p.crossover_date ?? null,
    sl_price: p.sl_price ?? null,
    target_price: p.target_price ?? null,
    sl_hit_date: p.sl_hit_date ?? null,
    high_hit_date: p.high_hit_date ?? null,
    target_hit_date: p.target_hit_date ?? null,
    trade_status: p.trade_status ?? "OPEN",
    days_to_target: p.days_to_target ?? null,
    entry_date: p.entry_date ?? null,
    exit_date: p.exit_date ?? null,
    exit_reason: p.exit_reason ?? null,
    exit_price: p.exit_price ?? null,
    holding_days: p.holding_days ?? null,
  };
};

const getStatusDisplay = (status) => {
  switch (status) {
    case "TARGET_HIT":
      return { label: "🎯 Target", cls: "sm__status--target" };
    case "SL_HIT":
      return { label: "⚠️ SL Hit", cls: "sm__status--sl" };
    case "SL_THEN_TARGET":
      return { label: "🟡 SL → Target", cls: "sm__status--sl-then-target" };
    case "OPEN":
      return { label: "⏳ Open", cls: "sm__status--open" };
    case "PENDING":
      return { label: "⏸ Not Entered", cls: "sm__status--pending" };
    default:
      return { label: status || "—", cls: "sm__status--open" };
  }
};

const TARGET_PCT = 0.05;
const computeTarget = (m, filter) => {
  const high = Number(m?.crossover_high);
  const low = Number(m?.crossover_low);
  let direction =
    filter === "Bullish"
      ? "bullish"
      : filter === "Bearish"
        ? "bearish"
        : m?.macd_crossover;
  if (direction === "bullish" && Number.isFinite(high))
    return Number((high * (1 + TARGET_PCT)).toFixed(2));
  if (direction === "bearish" && Number.isFinite(low))
    return Number((low * (1 - TARGET_PCT)).toFixed(2));
  return null;
};

// const GlobalCrossoverMomentum = ({ universe = "NASDAQ100" }) => {
//   const navigate = useNavigate();
//   const {
//     globalCrossover,
//     globalCrossoverLoading,
//     globalCrossoverError,
//     loadGlobalCrossover,
//   } = useMarketIndicators();

//   const { exportCSV, exportExcel, exporting } = useExport();

//   const meta = UNIVERSE_META[universe] || UNIVERSE_META.NASDAQ100;
//   const currency = "$";

//   const data = globalCrossover?.[universe];

//   const [sortKey, setSortKey] = useState("symbol");
//   const [sortDir, setSortDir] = useState("asc");
//   const [filter, setFilter] = useState("All");
//   const [timeframes, setTimeframes] = useState(["daily"]);

//   // ── one fetch per IST day (context handles the cache) ──
//   useEffect(() => {
//     loadGlobalCrossover(universe);
//   }, [universe, loadGlobalCrossover]);

//   const toggleTimeframe = (key) => {
//     setTimeframes((prev) =>
//       prev.includes(key)
//         ? prev.length > 1
//           ? prev.filter((t) => t !== key)
//           : prev
//         : [...prev, key],
//     );
//   };

//   const rows = data?.data || [];

//   const sorted = useMemo(() => {
//     const filtered =
//       filter === "All"
//         ? rows
//         : rows.filter((s) =>
//             timeframes.every((tf) => {
//               const pred = s?.predictions?.[tf]?.prediction?.toUpperCase?.();
//               if (filter === "Bullish") return pred === "BULLISH";
//               if (filter === "Bearish") return pred === "BEARISH";
//               return pred === "NEUTRAL" || !pred;
//             }),
//           );

//     return [...filtered].sort((a, b) => {
//       const mA = getMetrics(a, timeframes[0]);
//       const mB = getMetrics(b, timeframes[0]);
//       if (!mA && !mB) return 0;
//       if (!mA) return 1;
//       if (!mB) return -1;

//       if (sortKey === "symbol") {
//         const av = a?.symbol || "",
//           bv = b?.symbol || "";
//         return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
//       }

//       const dateKeys = [
//         "crossover_date",
//         "entry_date",
//         "exit_date",
//         "sl_hit_date",
//         "target_hit_date",
//         "high_hit_date",
//       ];
//       if (dateKeys.includes(sortKey)) {
//         const aT = mA?.[sortKey] ? new Date(mA[sortKey]).getTime() : null;
//         const bT = mB?.[sortKey] ? new Date(mB[sortKey]).getTime() : null;
//         if (aT == null && bT == null) return 0;
//         if (aT == null) return 1;
//         if (bT == null) return -1;
//         return sortDir === "asc" ? aT - bT : bT - aT;
//       }

//       if (sortKey === "prediction") {
//         const rank = { Bullish: 1, Neutral: 2, Bearish: 3 };
//         return sortDir === "asc"
//           ? (rank[mA?.prediction] || 99) - (rank[mB?.prediction] || 99)
//           : (rank[mB?.prediction] || 99) - (rank[mA?.prediction] || 99);
//       }
//       if (sortKey === "macd_crossover") {
//         const rank = { bullish: 1, none: 2, bearish: 3 };
//         return sortDir === "asc"
//           ? (rank[mA?.macd_crossover] || 99) - (rank[mB?.macd_crossover] || 99)
//           : (rank[mB?.macd_crossover] || 99) - (rank[mA?.macd_crossover] || 99);
//       }

//       const aV = mA?.[sortKey] ?? -Infinity;
//       const bV = mB?.[sortKey] ?? -Infinity;
//       return sortDir === "asc" ? aV - bV : bV - aV;
//     });
//   }, [rows, filter, sortKey, sortDir, timeframes]);

//   const handleRefresh = useCallback(() => {
//     loadGlobalCrossover(universe, { force: true });
//   }, [universe, loadGlobalCrossover]);

const GlobalCrossoverMomentum = () => {
  const navigate = useNavigate();

  const {
    globalCrossover,
    globalCrossoverLoading,
    globalCrossoverError,
    loadGlobalCrossover,
  } = useMarketIndicators();

  const { exportCSV, exportExcel, exporting } = useExport();

  const [selectedIndex, setSelectedIndex] = useState(
    () => localStorage.getItem("globalCrossoverSelectedIndex") || "NASDAQ100",
  );

  const meta = UNIVERSE_META[selectedIndex] || UNIVERSE_META.NASDAQ100;
  const currency = "$";

  const data = globalCrossover?.[selectedIndex];

  const [sortKey, setSortKey] = useState("symbol");
  const [sortDir, setSortDir] = useState("asc");
  const [filter, setFilter] = useState("All");
  const [timeframes, setTimeframes] = useState(["daily"]);

  useEffect(() => {
    loadGlobalCrossover(selectedIndex);
  }, [selectedIndex, loadGlobalCrossover]);

  const handleIndexChange = useCallback((e) => {
    const value = e.target.value;
    if (!value) return;

    localStorage.setItem("globalCrossoverSelectedIndex", value);
    setSelectedIndex(value);
  }, []);

  const toggleTimeframe = (key) => {
    setTimeframes((prev) =>
      prev.includes(key)
        ? prev.length > 1
          ? prev.filter((t) => t !== key)
          : prev
        : [...prev, key],
    );
  };

  const rows = data?.data || [];
  const { priceOf } = useGlobalLiveFeed(rows?.map((s) => s.symbol));
  const sorted = useMemo(() => {
    const filtered =
      filter === "All"
        ? rows
        : rows.filter((s) =>
            timeframes.every((tf) => {
              const pred = s?.predictions?.[tf]?.prediction?.toUpperCase?.();
              if (filter === "Bullish") return pred === "BULLISH";
              if (filter === "Bearish") return pred === "BEARISH";
              return pred === "NEUTRAL" || !pred;
            }),
          );

    return [...filtered].sort((a, b) => {
      const mA = getMetrics(a, timeframes[0]);
      const mB = getMetrics(b, timeframes[0]);
      if (!mA && !mB) return 0;
      if (!mA) return 1;
      if (!mB) return -1;

      if (sortKey === "symbol") {
        const av = a?.symbol || "",
          bv = b?.symbol || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }

      const dateKeys = [
        "crossover_date",
        "entry_date",
        "exit_date",
        "sl_hit_date",
        "target_hit_date",
        "high_hit_date",
      ];
      if (dateKeys.includes(sortKey)) {
        const aT = mA?.[sortKey] ? new Date(mA[sortKey]).getTime() : null;
        const bT = mB?.[sortKey] ? new Date(mB[sortKey]).getTime() : null;
        if (aT == null && bT == null) return 0;
        if (aT == null) return 1;
        if (bT == null) return -1;
        return sortDir === "asc" ? aT - bT : bT - aT;
      }

      if (sortKey === "prediction") {
        const rank = { Bullish: 1, Neutral: 2, Bearish: 3 };
        return sortDir === "asc"
          ? (rank[mA?.prediction] || 99) - (rank[mB?.prediction] || 99)
          : (rank[mB?.prediction] || 99) - (rank[mA?.prediction] || 99);
      }
      if (sortKey === "macd_crossover") {
        const rank = { bullish: 1, none: 2, bearish: 3 };
        return sortDir === "asc"
          ? (rank[mA?.macd_crossover] || 99) - (rank[mB?.macd_crossover] || 99)
          : (rank[mB?.macd_crossover] || 99) - (rank[mA?.macd_crossover] || 99);
      }

      const aV = mA?.[sortKey] ?? -Infinity;
      const bV = mB?.[sortKey] ?? -Infinity;
      return sortDir === "asc" ? aV - bV : bV - aV;
    });
  }, [rows, filter, sortKey, sortDir, timeframes]);

  const handleRefresh = useCallback(() => {
    loadGlobalCrossover(selectedIndex, { force: true });
  }, [selectedIndex, loadGlobalCrossover]);

  // Keep SortTh, export functions, and the table below.
  const SortTh = ({ label, field, align, tooltip, show = true }) => (
    <th className={align === "left" ? "sm__th-left" : ""}>
      <div className="sm__sort-head">
        {label}
        {tooltip && <InfoTooltip text={TOOLTIPS[tooltip]} />}
        {show && (
          <span className="sm__sort-arrows">
            <FaChevronUp
              size={10}
              className={sortKey === field && sortDir === "asc" ? "active" : ""}
              onClick={(e) => {
                e.stopPropagation();
                setSortKey(field);
                setSortDir("asc");
              }}
            />
            <FaChevronDown
              size={10}
              className={
                sortKey === field && sortDir === "desc" ? "active" : ""
              }
              onClick={(e) => {
                e.stopPropagation();
                setSortKey(field);
                setSortDir("desc");
              }}
            />
          </span>
        )}
      </div>
    </th>
  );

  const hideCrossoverLow = filter === "Bullish";
  const hideCrossoverHigh = filter === "Bearish";
  const showTarget = filter === "Bullish" || filter === "Bearish";

  const getLtp = (stock) => {
    // const v =
    //   stock?.ltp ??
    //   stock?.quote?.data?.regularMarketPrice ??
    //   stock?.quote?.data?.last_price ??
    //   null;
    const v =
      priceOf(stock?.symbol)?.ltp ?? // ← add this line
      stock?.ltp ??
      stock?.quote?.data?.regularMarketPrice ??
      stock?.quote?.data?.last_price ??
      null;
    const n = Number(v);
    return Number.isFinite(n) ? Number(n.toFixed(2)) : null;
  };
  const EXPORT_COLUMNS = [
    { header: "Symbol", key: "symbol", width: 15 },
    { header: "Company", key: "company", width: 22 },
    { header: "LTP", key: "ltp", width: 12 },
    { header: "Timeframe", key: "timeframe", width: 10 },
    { header: "RSI", key: "rsi", width: 10 },
    { header: "MACD", key: "macd", width: 12 },
    { header: "Signal Line", key: "macd_signal", width: 12 },
    { header: "Histogram", key: "histogram", width: 12 },
    { header: "Crossover", key: "macd_crossover", width: 12 },
    { header: "Crossover Date", key: "crossover_date", width: 15 },
    { header: "Crossover Close", key: "crossover_close", width: 14 },
    { header: "Crossover High", key: "crossover_high", width: 14 },
    { header: "Crossover Low", key: "crossover_low", width: 14 },
    { header: "SL", key: "sl_price", width: 12 },
    { header: "Target", key: "target_price", width: 12 },
    { header: "Status", key: "trade_status", width: 14 },
    { header: "Momentum", key: "prediction", width: 12 },
    { header: "Result", key: "prediction_result", width: 14 },
  ];

  const buildExportRows = () => {
    const out = [];
    sorted.forEach((stock) => {
      // const ltp =
      //   stock?.quote?.data?.last_price ?? stock?.quote?.last_price ?? null;
      const ltp = getLtp(stock);
      timeframes
        .filter((tf) => getMetrics(stock, tf))
        .forEach((tf) => {
          const m = getMetrics(stock, tf);
          out.push({
            symbol: stock.symbol,
            company: stock.company || "",
            ltp: ltp ?? "",
            timeframe: tf,
            rsi: m?.rsi ?? "",
            macd: m?.macd ?? "",
            macd_signal: m?.macd_signal ?? "",
            histogram: m?.histogram ?? "",
            macd_crossover: m?.macd_crossover ?? "",
            crossover_date: m?.crossover_date ?? "",
            crossover_close: m?.crossover_close ?? "",
            crossover_high: m?.crossover_high ?? "",
            crossover_low: m?.crossover_low ?? "",
            sl_price: m?.sl_price ?? "",
            target_price: m?.target_price ?? "",
            trade_status: m?.trade_status ?? "",
            target: computeTarget(m, filter) ?? "",
            prediction: m?.prediction ?? "",
            prediction_result: m?.prediction_result ?? "",
          });
        });
    });
    return out;
  };

  const filterSuffix = showTarget ? `_${filter.toLowerCase()}` : "";
  const exportColumns = showTarget
    ? [...EXPORT_COLUMNS, { header: "Filter Target", key: "target", width: 12 }]
    : EXPORT_COLUMNS;

  const handleDownloadCSV = () =>
    exportCSV({
      rows: buildExportRows(),
      columns: exportColumns,
      // filename: `${universe}${filterSuffix}_crossover`,
      filename: `${selectedIndex}${filterSuffix}_crossover`,
      sheetName: selectedIndex,
      // sheetName: universe,
    });

  const handleDownloadExcel = () =>
    exportExcel({
      rows: buildExportRows(),
      columns: exportColumns,
      // filename: `${universe}${filterSuffix}_crossover`,
      // sheetName: universe,
      filename: `${selectedIndex}${filterSuffix}_crossover`,
      sheetName: selectedIndex,
    });

  return (
    <div className="sm">
      {/* ── Header ── */}
      <div className="sm__header">
        <div className="sm__header-left">
          <div className="sm__title-row">
            <button className="sm__back" onClick={() => navigate(-1)}>
              ←
            </button>
            <h1 className="sm__title">{meta.title}</h1>
          </div>
          <p className="sm__sub">{meta.subtitle}</p>
          {data?.date && (
            <div className="sm__condition-pill" style={{ marginTop: 6 }}>
              <span>Universe</span>
              <span style={{ margin: "0 6px" }}>=</span>
              <span style={{ color: "#249966", fontWeight: 600 }}>
                {/* {universe} */}
                {selectedIndex}
              </span>
              <span style={{ marginLeft: 10, color: "#6b7280", fontSize: 12 }}>
                as of {data.date}
              </span>
            </div>
          )}
        </div>

        <div className="sm__header-right">
          <select
            className="sm__index-select"
            value={selectedIndex}
            onChange={handleIndexChange}
          >
            {Object.entries(UNIVERSE_META).map(([key, item]) => (
              <option key={key} value={key}>
                {item.label}
              </option>
            ))}
          </select>
          <button
            className="sm__refresh-btn"
            onClick={handleRefresh}
            disabled={globalCrossoverLoading}
          >
            <FiRefreshCw size={14} />
          </button>
          <button
            className="sm__refresh-btn"
            onClick={handleDownloadCSV}
            disabled={exporting || !sorted.length}
          >
            {exporting ? "…" : "CSV ⬇"}
          </button>
          <button
            className="sm__refresh-btn"
            onClick={handleDownloadExcel}
            disabled={exporting || !sorted.length}
          >
            {exporting ? "…" : "Excel ⬇"}
          </button>
        </div>
      </div>

      {/* ── Meta bar ── */}
      <div className="sm__result-bar">
        <div
          className="sm__timeframe-group"
          style={{ display: "flex", gap: 4 }}
        >
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.key}
              className={`sm__filter-btn ${timeframes.includes(tf.key) ? "active" : ""}`}
              onClick={() => toggleTimeframe(tf.key)}
            >
              {tf.label}
            </button>
          ))}
        </div>

        <div
          className="sm__signal-filter-group"
          style={{ display: "flex", gap: 4 }}
        >
          {["All", "Bullish", "Neutral", "Bearish"].map((f) => (
            <button
              key={f}
              className={`sm__filter-btn ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>

        <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: 13 }}>
          <b>{sorted.length}</b> stocks
        </span>
      </div>

      {/* ── Loading / Error ── */}
      {globalCrossoverLoading && !sorted.length && (
        <div className="sm__loading">Loading {selectedIndex} crossovers...</div>
      )}
      {globalCrossoverError && (
        <div className="sm__empty">
          <div className="sm__empty-title">{globalCrossoverError}</div>
        </div>
      )}

      {/* ── Table ── */}
      {sorted.length > 0 && (
        <div className="sm__table-wrap">
          <table className="sm__table">
            <thead>
              <tr>
                <SortTh label="Symbol" field="symbol" align="left" />
                <th>LTP</th>
                <SortTh
                  label="TF"
                  field="timeframe"
                  tooltip="tf"
                  show={false}
                />
                <SortTh
                  label="Crossover"
                  field="macd_crossover"
                  tooltip="macdCrossover"
                />
                <SortTh label="Crossover Date" field="crossover_date" />
                <th>Crossover Close</th>
                {!hideCrossoverHigh && <th>Crossover High</th>}
                {!hideCrossoverLow && <th>Crossover Low</th>}
                {showTarget && <th>Filter Target</th>}

                <SortTh label="SL" field="sl_price" />
                <SortTh label="Target" field="target_price" />
                <SortTh label="SL Hit Date" field="sl_hit_date" />
                <SortTh label="High Hit Date" field="high_hit_date" />
                <SortTh label="Entry Date" field="entry_date" />
                <SortTh label="Exit Date" field="exit_date" />
                <SortTh label="Target Hit Date" field="target_hit_date" />
                <th>Status</th>

                <SortTh
                  label="Momentum"
                  field="prediction"
                  tooltip="momentum"
                />
                <SortTh
                  label="Result"
                  field="result"
                  tooltip="momentumResult"
                />
                <SortTh label="RSI" field="rsi" />
                <SortTh label="MACD" field="macd" />
                <SortTh
                  label="Signal Line"
                  field="macd_signal"
                  tooltip="macdSignalLine"
                />
                <SortTh
                  label="Histogram"
                  field="histogram"
                  tooltip="macdHistogram"
                />
              </tr>
            </thead>

            <tbody>
              {sorted.map((stock) => {
                const activeTFs = timeframes.filter((tf) =>
                  getMetrics(stock, tf),
                );
                // const ltp =
                //   stock?.quote?.data?.last_price ??
                //   stock?.quote?.last_price ??
                //   null;
                const ltp = getLtp(stock);
                if (!activeTFs.length) return null;

                return activeTFs.map((tf, i) => {
                  const m = getMetrics(stock, tf);
                  const isFirst = i === 0;
                  const statusInfo = getStatusDisplay(m.trade_status);
                  const isYellow = m.trade_status === "SL_THEN_TARGET";

                  return (
                    <tr
                      key={`${stock.symbol}-${tf}`}
                      className={`sm__row ${isYellow ? "sm__row--yellow" : ""}`}
                      onClick={() => navigate(`/globaltest/${stock.symbol}`)}
                    >
                      <td className="sm__td-name">
                        {isFirst ? (
                          <div>
                            <span className="sm__symbol">{stock.symbol}</span>
                            {stock.company && (
                              <small
                                style={{
                                  display: "block",
                                  color: "#6b7280",
                                  fontSize: 11,
                                }}
                              >
                                {stock.company}
                              </small>
                            )}
                          </div>
                        ) : null}
                      </td>

                      <td>{ltp ? `${currency}${ltp}` : "—"}</td>

                      <td
                        className="sm__td-num"
                        style={{ fontSize: 11, fontWeight: 600 }}
                      >
                        {tf[0].toUpperCase()}
                      </td>

                      <td className="sm__td-num">
                        <span
                          className={`sm__signal-pill ${getCrossoverClass(m?.macd_crossover)}`}
                        >
                          {m?.macd_crossover === "bullish"
                            ? "↑ Bullish"
                            : m?.macd_crossover === "bearish"
                              ? "↓ Bearish"
                              : "—"}
                        </span>
                      </td>

                      <td className="sm__td-num">{m.crossover_date || "—"}</td>
                      <td className="sm__td-num">
                        ${m.crossover_close ?? "—"}
                      </td>
                      {!hideCrossoverHigh && (
                        <td className="sm__td-num">
                          ${m.crossover_high ?? "—"}
                        </td>
                      )}
                      {!hideCrossoverLow && (
                        <td className="sm__td-num">
                          ${m.crossover_low ?? "—"}
                        </td>
                      )}
                      {showTarget && (
                        <td
                          className="sm__td-num"
                          style={{
                            fontWeight: 600,
                            color: filter === "Bearish" ? "#f87171" : "#249966",
                          }}
                        >
                          {(() => {
                            const t = computeTarget(m, filter);
                            return t != null ? `${currency}${t}` : "—";
                          })()}
                        </td>
                      )}

                      <td
                        className="sm__td-num"
                        style={{ color: "#f87171", fontWeight: 600 }}
                      >
                        {m.sl_price ? `${currency}${m.sl_price}` : "—"}
                      </td>
                      <td
                        className="sm__td-num"
                        style={{ color: "#249966", fontWeight: 600 }}
                      >
                        {m.target_price ? `${currency}${m.target_price}` : "—"}
                      </td>
                      <td className="sm__td-num">{m.sl_hit_date ?? "—"}</td>
                      <td className="sm__td-num">{m.high_hit_date ?? "—"}</td>
                      <td className="sm__td-num">{m.entry_date ?? "—"}</td>
                      <td className="sm__td-num">
                        {m.exit_date ? (
                          <>
                            {m.exit_date}
                            {m.exit_reason && (
                              <small
                                style={{
                                  display: "block",
                                  color:
                                    m.exit_reason === "SL"
                                      ? "#f87171"
                                      : "#249966",
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                {m.exit_reason === "SL" ? "SL" : "🎯 Target"}
                                {m.holding_days != null &&
                                  ` · ${m.holding_days}d`}
                              </small>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="sm__td-num">{m.target_hit_date ?? "—"}</td>

                      <td className="sm__td-num">
                        <span className={`sm__status ${statusInfo.cls}`}>
                          {statusInfo.label}
                        </span>
                        {m.trade_status === "OPEN" && m.crossover_date && (
                          <small
                            style={{
                              color: "#9ca3af",
                              marginLeft: 4,
                              fontSize: 11,
                            }}
                          >
                            (
                            {Math.round(
                              (Date.now() -
                                new Date(m.crossover_date).getTime()) /
                                86400000,
                            )}
                            d)
                          </small>
                        )}
                      </td>

                      <td className="sm__td-num">
                        <span
                          className={`sm__signal-badge ${getBadgeClass(m?.prediction)}`}
                        >
                          {m?.prediction || "—"}
                        </span>
                      </td>
                      <td
                        className="sm__td-num"
                        style={{ fontSize: 12, color: "#6b7280" }}
                      >
                        {m?.prediction_result || "—"}
                      </td>

                      <td
                        className="sm__td-num"
                        style={{
                          color:
                            m?.rsi > 55
                              ? "#249966"
                              : m?.rsi < 45
                                ? "#f87171"
                                : "#64748b",
                          fontWeight: 600,
                        }}
                      >
                        {m?.rsi ?? "—"}
                      </td>
                      <td
                        className="sm__td-num"
                        style={{
                          color:
                            m?.macd > 0
                              ? "#249966"
                              : m?.macd < 0
                                ? "#f87171"
                                : "#64748b",
                          fontWeight: 600,
                        }}
                      >
                        {m?.macd ?? "—"}
                      </td>
                      <td className="sm__td-num" style={{ color: "#64748b" }}>
                        {m?.macd_signal ?? "—"}
                      </td>
                      <td
                        className="sm__td-num"
                        style={{
                          color:
                            m?.histogram > 0
                              ? "#249966"
                              : m?.histogram < 0
                                ? "#f87171"
                                : "#64748b",
                          fontWeight: 600,
                        }}
                      >
                        {m?.histogram ?? "—"}
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      )}

      {!globalCrossoverLoading && !sorted.length && !globalCrossoverError && (
        <div className="sm__empty">
          <div className="sm__empty-icon">📊</div>
          <div className="sm__empty-title">No data available</div>
          <div className="sm__empty-sub">
            Try refreshing — the daily cron may not have populated{" "}
            {selectedIndex} yet
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalCrossoverMomentum;
